import { useEffect, type RefObject } from 'react';

/** What counts as reachable by keyboard. `:not([disabled])` matters on the
 *  form controls; `[tabindex]:not([tabindex="-1"])` catches anything the host
 *  made focusable deliberately, and excludes the container's own -1. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusable(root: HTMLElement): HTMLElement[] {
  // offsetParent filters out anything display:none'd; a zero-size element is
  // focusable in the DOM but sending focus to it looks like focus vanishing.
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed',
  );
}

/**
 * Hold keyboard focus inside `ref` while `active`, and give it back to
 * whatever had it when the trap closed.
 *
 * This is the FIRST focus trap in the repo — the drawers and modals all let
 * Tab wander behind them onto the page they cover. It lives here rather than
 * in the studio because the sheet it exists for renders in both web apps.
 *
 * Three things it does, in the order they matter:
 *
 * - **Remembers the trigger itself**, not "the previously focused element" at
 *   some later moment. It is read once on activation, because by the time the
 *   panel has mounted and taken focus the answer has already changed.
 * - **Wraps Tab at both ends.** Only the two edges need handling: everything
 *   between them is the browser's own order, and re-implementing that is how
 *   traps end up disagreeing with the platform about radio groups and
 *   shadow roots.
 * - **Re-reads the focusable list on every keypress.** A sheet's contents can
 *   change while it is open (a submenu expands, a row disables), and a list
 *   captured at mount would send Tab to an element that has since gone.
 *
 * Escape is deliberately NOT handled here — the host owns closing, and a
 * nested dialog needs to decide for itself which layer an Escape belongs to.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;

    const returnTo = document.activeElement as HTMLElement | null;

    // Focus the first real control, falling back to the container (which the
    // host gives tabIndex={-1}) so focus is never left outside the trap.
    const first = focusable(root)[0];
    (first ?? root).focus();

    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const items = focusable(root!);
      if (items.length === 0) {
        // Nothing to move between: keep focus on the container rather than
        // letting Tab escape to the page behind.
        e.preventDefault();
        root!.focus();
        return;
      }
      const firstItem = items[0]!;
      const lastItem = items[items.length - 1]!;
      const current = document.activeElement;
      // Focus sitting on the container (or somewhere outside) counts as "before
      // the first item", so Tab enters the trap instead of leaving it.
      if (e.shiftKey && (current === firstItem || !root!.contains(current))) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && current === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Only take focus back if nothing else has claimed it. A toast or a
      // nested dialog that outlived us owns it now, and yanking it away would
      // be the more surprising behaviour.
      //
      // `document.body` counts as ours, and that case is the common one, not
      // an edge: by the time this cleanup runs React has usually detached the
      // panel, so the element that had focus is gone and the browser has
      // already fallen back to body. Testing only for `root.contains(...)`
      // therefore never restored anything on a normal close.
      const now = document.activeElement;
      const stillOurs = !now || now === document.body || root.contains(now);
      if (returnTo && stillOurs) returnTo.focus();
    };
  }, [ref, active]);
}
